import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

import styles from './AccountMenu.module.css';

export interface AccountMenuProps {
  /** The name drawn on the trigger. */
  name: string;
  /** Shown under the name inside the menu when it adds something. */
  email?: string | null;
  onSettings?: () => void;
  onApiKeys?: () => void;
  onSignOut?: () => void;
}

function initialsOf(label: string): string {
  const local = label.includes('@') ? (label.split('@')[0] ?? '') : label;
  const parts = local.split(/[\s._-]+/).filter(Boolean);
  const letters = parts.length > 1 ? parts[0][0] + parts[1][0] : local.slice(0, 2);
  return letters.toUpperCase();
}

/** The signed-in name top-right, opening a menu of account actions. */
export function AccountMenu({ name, email, onSettings, onApiKeys, onSignOut }: AccountMenuProps) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) {
      return;
    }
    menu.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const onPointer = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open]);

  function choose(action?: () => void) {
    setOpen(false);
    action?.();
  }

  function onMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const items = Array.from(
      menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    const index = items.indexOf(document.activeElement as HTMLElement);
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      trigger.current?.focus();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      items[(index + step + items.length) % items.length]?.focus();
    } else if (event.key === 'Tab') {
      setOpen(false);
    }
  }

  return (
    <div className={styles.root} ref={root}>
      <button
        ref={trigger}
        type="button"
        className={styles.trigger}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen(!open)}
      >
        <span className={styles.avatar} aria-hidden="true">
          {initialsOf(name)}
        </span>
        <span className={styles.name}>{name}</span>
        <span className={styles.caret} aria-hidden="true">
          ▾
        </span>
      </button>
      {open ? (
        <div
          ref={menu}
          id={menuId}
          role="menu"
          aria-label="Account"
          className={styles.menu}
          onKeyDown={onMenuKeyDown}
        >
          {email && email !== name ? (
            <div className={styles.header} role="presentation">
              <span className={styles.headerName}>{name}</span>
              <span className={styles.headerEmail} title={email}>
                {email}
              </span>
            </div>
          ) : null}
          {onSettings ? (
            <button
              type="button"
              role="menuitem"
              tabIndex={-1}
              className={styles.item}
              onClick={() => choose(onSettings)}
            >
              Settings
            </button>
          ) : null}
          {onApiKeys ? (
            <button
              type="button"
              role="menuitem"
              tabIndex={-1}
              className={styles.item}
              onClick={() => choose(onApiKeys)}
            >
              API Keys
            </button>
          ) : null}
          {onSignOut ? (
            <button
              type="button"
              role="menuitem"
              tabIndex={-1}
              className={styles.item}
              onClick={() => choose(onSignOut)}
            >
              Sign out
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
